/**
 * SPC & process capability — a scientific analysis workspace.
 *
 * Honesty about derivability is the design constraint: Cpk/Ppk are computed
 * only from measurements the plant supplies; small samples say so; OEE
 * availability/performance stay unavailable until EUROMAP 77 telemetry exists.
 * Progressive disclosure: the first-time reader gets plain-language
 * interpretation; the expert opens the statistical detail.
 */
import { useState } from 'react';
import { api, qs } from '../api/client';
import { useApi } from '../hooks/useApi';
import type { CapabilityResult } from '../api/types';
import { Card, ErrorBox, Loading, Metric, Unavailable } from '../components/Metric';
import { RunChart } from '../components/charts';

export function SpcPage() {
  const [characteristic, setCharacteristic] = useState('Wall thickness');
  const [unit, setUnit] = useState('mm');
  const [lsl, setLsl] = useState('9.7');
  const [usl, setUsl] = useState('10.3');
  const [values, setValues] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);

  const result = useApi<CapabilityResult>(
    () =>
      submitted
        ? api.get<CapabilityResult>(
            `/api/v1/metrics/capability${qs({
              characteristic,
              unit,
              upperSpecLimit: usl,
              lowerSpecLimit: lsl,
              values: submitted,
            })}`,
          )
        : Promise.reject(new Error('not submitted')),
    [submitted, usl, lsl, characteristic, unit],
  );

  function submit() {
    setFail(null);
    const nums = values
      .split(/[,\s]+/)
      .map((v) => v.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => Number.isFinite(n));
    if (nums.length === 0) {
      setFail('Enter at least one numeric measurement.');
      return;
    }
    if (!Number.isFinite(Number(usl)) || !Number.isFinite(Number(lsl))) {
      setFail('Both specification limits must be numbers. A capability index without limits is not a capability index.');
      return;
    }
    setSubmitted(nums.join(','));
  }

  const r = result.data;
  const nums = submitted?.split(',').map(Number) ?? [];

  return (
    <>
      <Card
        title="Compute capability from your measurements"
        note="Paste measurements oldest-first, separated by commas or spaces. Cpk uses within-subgroup sigma (moving-range / 1.128); Ppk uses overall sigma. Both are shown because they answer different questions."
      >
        <div className="grid grid-4">
          <div className="field">
            <label htmlFor="spc-char">Characteristic</label>
            <input id="spc-char" value={characteristic} onChange={(e) => setCharacteristic(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="spc-unit">Unit</label>
            <input id="spc-unit" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="spc-lsl">Lower spec limit</label>
            <input id="spc-lsl" value={lsl} onChange={(e) => setLsl(e.target.value)} inputMode="decimal" />
          </div>
          <div className="field">
            <label htmlFor="spc-usl">Upper spec limit</label>
            <input id="spc-usl" value={usl} onChange={(e) => setUsl(e.target.value)} inputMode="decimal" />
          </div>
        </div>
        <div className="field">
          <label htmlFor="spc-values">Measurements</label>
          <textarea
            id="spc-values"
            rows={4}
            value={values}
            placeholder="10.02, 9.98, 10.05, 9.97, 10.01, …"
            onChange={(e) => setValues(e.target.value)}
            className="mono"
          />
          <div className="hint">
            At least 2 values are needed for any index; fewer than about 25 gives
            a wide confidence interval and the page will say so.
          </div>
        </div>
        {fail && <div className="error-box">{fail}</div>}
        <button className="btn primary" onClick={submit}>
          Compute
        </button>
      </Card>

      {submitted && (
        <div style={{ marginTop: 14 }}>
          {result.loading && <Loading label="Computing…" />}
          {result.error && <ErrorBox message={result.error} />}
          {r && (
            <>
              <div className="grid grid-4">
                <Metric
                  label="Cpk (within)"
                  tone={r.cpk == null ? 'default' : r.cpk < 1 ? 'danger' : r.cpk < 1.33 ? 'warn' : 'ok'}
                  value={r.cpk != null ? r.cpk.toFixed(2) : undefined}
                  from="Within-subgroup variation"
                >
                  <Unavailable reason="Fewer than 2 measurements." />
                </Metric>
                <Metric
                  label="Ppk (overall)"
                  tone={r.ppk == null ? 'default' : r.ppk < 1 ? 'danger' : r.ppk < 1.33 ? 'warn' : 'ok'}
                  value={r.ppk != null ? r.ppk.toFixed(2) : undefined}
                  from="Overall variation"
                >
                  <Unavailable reason="Fewer than 2 measurements." />
                </Metric>
                <Metric label="Mean" value={r.mean != null ? r.mean.toFixed(4) : undefined} unit={r.unit ?? undefined} from="Arithmetic mean of samples">
                  <Unavailable reason="No data." />
                </Metric>
                <Metric
                  label="n"
                  value={r.n}
                  sub={r.n < 25 ? 'Small sample — wide confidence interval' : undefined}
                  from="Samples supplied"
                />
              </div>

              <div className="card" style={{ marginTop: 14 }}>
                <div className="card-head">
                  <h3>Run chart</h3>
                  <span className="tag">oldest → newest</span>
                </div>
                {nums.length > 0 && (
                  <RunChart
                    values={nums}
                    lsl={Number(lsl)}
                    usl={Number(usl)}
                    mean={r.mean}
                    unit={r.unit ?? unit}
                  />
                )}
                <div className="card-note">
                  Points outside the dashed limits are out of specification and
                  are drawn in red. The grey line is the sample mean.
                </div>
              </div>

              <div className="callout info" style={{ marginTop: 14 }}>
                <strong>Interpretation</strong>
                {r.interpretation}
              </div>

              <details className="raw card" style={{ padding: 14 }}>
                <summary>Statistical detail (σ, method)</summary>
                <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ink-2)' }}>
                  {r.sigmaWithin != null ? (
                    <p>
                      σ within = <span className="mono">{r.sigmaWithin.toFixed(5)}</span>
                      {r.sigmaOverall != null && (
                        <>
                          {' '}· σ overall = <span className="mono">{r.sigmaOverall.toFixed(5)}</span>
                        </>
                      )}
                    </p>
                  ) : (
                    <p>Not enough samples for a sigma estimate.</p>
                  )}
                  <p style={{ marginTop: 6 }}>
                    A large gap between σ within and σ overall indicates the
                    process is shifting over time rather than merely varying.
                    Cpk answers “can the process fit inside the limits right
                    now?”; Ppk answers “how did it actually perform over the
                    whole sample?”
                  </p>
                </div>
              </details>
            </>
          )}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <Card
          title="Why availability and performance are not shown here"
          note="This is a scope statement, not a gap in the UI."
        >
          <p style={{ maxWidth: '86ch', margin: 0 }}>
            <strong>OEE availability and performance require EUROMAP 77 telemetry.</strong>{' '}
            Quality rate can be derived from inspection results alone, so it is
            reported on the plant overview. Availability needs real runtime and
            downtime data; performance needs ideal cycle time against actual.
            Both come from EUROMAP 77 (OPC UA) machine telemetry. Until that is
            connected, OEE is reported as unavailable rather than computed with
            those factors assumed to be 100% — which would overstate OEE for
            every machine on the floor.
          </p>
        </Card>
      </div>
    </>
  );
}
